import { isBenchmark } from './checks'
import { forLetterStore } from '@aytm/app/insights/dashboard/lib/letters'
import { addFiltersValue, deleteFiltersValue } from '@aytm/app/insights/dashboard/lib/filters'
import { updatePortalFilters, getPortalFilters } from './filters'

import { Data, SubjectsType, SubjectType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import { BlockModel } from '@aytm/app/insights/dashboard/models/BlockModel'
import { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'

import { getCurrentBlock, updateCurrentBlock, updateCurrentData } from './requests'

import { setInImmutably, deleteInImmutably } from './objects'

const lettersBuilder = (block: BlockModel) => {
  const letterStore = block?.getObject('letterStore') || {}
  const getSubjectLetter = forLetterStore(letterStore).getSubjectLetter
  return (uid: string) => getSubjectLetter(uid, block)
}

export const addSubjectItem = (state: InsightsDashboardPoolState, subject: SubjectType): InsightsDashboardPoolState => {
  let resultState = state
  const subjects = state.currentData?.subjects || {}
  const data = state.currentData?.data || {}
  const block = getCurrentBlock(state)
  const items = block?.getItemsArray()
  if (!items?.some(v => subject.uid === v.uid)) {
    if (!subjects[subject.uid]) {
      // Add new subject to subjects list
      const newSubjects = setInImmutably(subjects, [subject.uid], subject) as SubjectsType
      resultState = updateCurrentData(resultState, { subjects: newSubjects })
    }
    if (!data[subject.uid]) {
      // Add new subject to data list
      const newData = setInImmutably(data, [subject.uid, 'attributes'], subject.aggregatedValues.values) as Data
      resultState = updateCurrentData(resultState, { data: newData })
    }
    const getLetter = lettersBuilder(block)
    const isBenchmarkFlag = isBenchmark({ uid: subject.uid })
    const letter = getLetter(subject.uid)
    // Append new subject to items list
    const newItems = items.slice()
    newItems.push({
      uid: subject.uid,
      letter,
      pinned: isBenchmarkFlag,
      new: isBenchmarkFlag
    })
    resultState = updateCurrentBlock(resultState, { items: newItems })
    if (isBenchmarkFlag) {
      // Add new benchmark to filters
      const filters = getPortalFilters(resultState)
      const newFilters = addFiltersValue(filters, 'benchmark:uid', subject.uid)
      resultState = updatePortalFilters(resultState, newFilters)
      resultState = updateCurrentData(resultState, { portalCurrentFilters: newFilters.clone() })
    }
  }
  return resultState
}

export const updateSubjectItem = (
  state: InsightsDashboardPoolState,
  subject: SubjectType
): InsightsDashboardPoolState => {
  let resultState = state
  const subjects = state.currentData?.subjects || {}
  const data = state.currentData?.data || {}
  const block = getCurrentBlock(state)
  const items = block?.getItemsArray()
  if (items?.some(v => subject.uid === v.uid)) {
    // Update subject subjects list
    const newSubjects = setInImmutably(subjects, [subject.uid], subject) as SubjectsType
    resultState = updateCurrentData(resultState, { subjects: newSubjects })
    // Update data
    const newData = setInImmutably(data, [subject.uid, 'attributes'], subject.aggregatedValues.values) as Data
    resultState = updateCurrentData(resultState, { data: newData })
  }
  return resultState
}

export const changeSubjectItem = (
  state: InsightsDashboardPoolState,
  subject: SubjectType
): InsightsDashboardPoolState => {
  let resultState = state
  const subjects = state.currentData?.subjects || {}
  const block = getCurrentBlock(state)
  const items = block?.getItemsArray()
  if (items?.some(v => subject.uid === v.uid)) {
    // Update subject subjects list
    const newSubjects = setInImmutably(subjects, [subject.uid], subject) as SubjectsType
    resultState = updateCurrentData(resultState, { subjects: newSubjects })
  }
  return resultState
}

export const deleteSubjectItem = (
  state: InsightsDashboardPoolState,
  subject: SubjectType
): InsightsDashboardPoolState => {
  let resultState = state
  const subjects = state.currentData?.subjects || {}
  const data = state.currentData?.data || {}
  const block = getCurrentBlock(state)
  const items = block?.getItemsArray()
  if (items?.some(v => subject.uid === v.uid)) {
    // Delete subject from items list
    const newItems = items.filter(s => s.uid !== subject.uid)
    resultState = updateCurrentBlock(resultState, { items: newItems })
    if (isBenchmark({ uid: subject.uid })) {
      // Remove benchmark from filters
      const filters = getPortalFilters(resultState)
      const newFilters = deleteFiltersValue(filters, 'benchmark:uid', subject.uid)
      resultState = updatePortalFilters(resultState, newFilters)
      resultState = updateCurrentData(resultState, { portalCurrentFilters: newFilters.clone() })
    }
  }
  if (data[subject.uid]) {
    // Remove subject from data list
    const newData = deleteInImmutably(data, [subject.uid]) as Data
    resultState = updateCurrentData(resultState, { data: newData })
  }
  if (subjects[subject.uid]) {
    // Remove subject from subjects list
    const newSubjects = deleteInImmutably(subjects, [subject.uid]) as SubjectsType
    resultState = updateCurrentData(resultState, { subjects: newSubjects })
  }

  return resultState
}

export type UpdateAction = 'add' | 'update' | 'change' | 'delete'

export const addOrUpdateSubject = (
  state: InsightsDashboardPoolState,
  subject: SubjectType,
  action: UpdateAction
): InsightsDashboardPoolState => {
  let resultState = state
  if ('add' === action) {
    // Add new subject to items list
    resultState = addSubjectItem(resultState, subject)
  } else if ('update' === action) {
    // Update existing subject
    resultState = updateSubjectItem(resultState, subject)
  } else if ('change' === action) {
    // Change existing subject
    resultState = changeSubjectItem(resultState, subject)
  } else if ('delete' === action) {
    // Remove subject from subjects list
    resultState = deleteSubjectItem(resultState, subject)
  }
  return resultState
}
